import pako from "pako";

/**
 * Decompress function:
 * - Accepts base64 compressed string (result of compressWith2Bytes / compressWith4Bytes)
 * - Inflates to Uint8Array, then tries reading header using 4 bytes first, then 2 bytes
 * - Returns the original JSON string or null on failure
 */
export const decompress = (compressedData: string): string | null => {
  try {
    // base64 -> binary -> Uint8Array
    const binary = atob(compressedData);
    const input = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) input[i] = binary.charCodeAt(i);

    // Inflate to Uint8Array (raw bytes)
    const inflated: Uint8Array = pako.inflate(input);

    // Try 4-byte header then 2-byte header
    try {
      return internalDecompressFromUint8(inflated, 4);
    } catch (err4) {
      console.warn("decompress: 4-byte header decode failed:", err4);
      try {
        return internalDecompressFromUint8(inflated, 2);
      } catch (err2) {
        console.error("decompress: 2-byte header decode failed:", err2);
        return null;
      }
    }
  } catch (e) {
    console.error("decompress: top-level failure:", e);
    return null;
  }
};

/**
 * Internal helper: reads prefixLength bytes from inflated Uint8Array,
 * extracts the payload, applies inverse transform and returns original string.
 */
const internalDecompressFromUint8 = (inflated: Uint8Array, prefixLength: number): string => {
  if (!inflated || inflated.length < prefixLength) {
    throw new Error(`inflated too short for prefixLength=${prefixLength}`);
  }

  // Read length from first N bytes (big-endian)
  let length = 0;
  for (let i = 0; i < prefixLength; i++) {
    length = (length << 8) | inflated[i];
  }

  if (length < 0 || prefixLength + length > inflated.length) {
    throw new Error(`invalid length=${length} for prefixLength=${prefixLength}, inflated.length=${inflated.length}`);
  }

  // Extract payload bytes that were transformed earlier
  const payloadBytes = inflated.slice(prefixLength, prefixLength + length);

  // Convert payload bytes to string
  let encryptedStr = "";
  for (let i = 0; i < payloadBytes.length; i++) encryptedStr += String.fromCharCode(payloadBytes[i]);

  // reverse and inverse offset (same as your Java inverse)
  const reversed = encryptedStr.split("").reverse().join("");
  const OFFSET = 4;
  let tmp = "";
  for (let i = 0; i < reversed.length; i++) {
    tmp += String.fromCharCode(reversed.charCodeAt(i) - OFFSET);
  }

  // tmp is base64 of original -> decode
  const original = atob(tmp);
  return original;
};

/* -------------------- Compression helpers -------------------- */

/**
 * Compress using 4-byte prefix header (for Atlas)
 */
export const compressWith4Bytes = (content: string): string | null => internalCompress(content, 4);

/**
 * Compress using 2-byte prefix header (for SmartDoc)
 */
export const compressWith2Bytes = (content: string): string | null => internalCompress(content, 2);

/**
 * internal compress: returns base64 string
 */
const internalCompress = (content: string, prefixLength: number): string | null => {
  try {
    // Inverse encrypt (base64 -> reverse -> +OFFSET -> prefix length)
    const encryptedUint8 = getInverseEncryptAsUint8(content, prefixLength);

    // deflate (Uint8Array)
    const compressed: Uint8Array = pako.deflate(encryptedUint8);

    // Convert compressed Uint8Array to base64 safely (avoid spread)
    const compressedStr = String.fromCharCode.apply(null, Array.from(compressed));
    return btoa(compressedStr);
  } catch (e) {
    console.error(`internalCompress failed (prefix=${prefixLength}):`, e);
    return null;
  }
};

/**
 * Create the transformed payload as Uint8Array:
 * - base64(input)
 * - reverse
 * - add OFFSET (char+OFFSET)
 * - prefix length bytes (big-endian)
 */
const getInverseEncryptAsUint8 = (line: string, prefixLength: number): Uint8Array => {
  const b64encoded = btoa(line);
  const reversed = b64encoded.split("").reverse().join("");

  const OFFSET = 4;
  let transformed = "";
  for (let i = 0; i < reversed.length; i++) {
    transformed += String.fromCharCode(reversed.charCodeAt(i) + OFFSET);
  }

  const length = transformed.length;
  const header = new Uint8Array(prefixLength);
  for (let i = 0; i < prefixLength; i++) {
    header[i] = (length >> (8 * (prefixLength - 1 - i))) & 0xff;
  }

  const result = new Uint8Array(prefixLength + length);
  for (let i = 0; i < prefixLength; i++) result[i] = header[i];
  for (let i = 0; i < length; i++) result[i + prefixLength] = transformed.charCodeAt(i);

  return result;
};
